import type { AshtakootaResult, KootaScore } from '@astrally/shared';
import { InfoDot } from '../../components/common';
import { Term } from '../../components/Term';

/**
 * The classical 36-guna table (Ashtakoota / Guna Milan), rendered from the
 * engine's own output.
 *
 * Nothing here calculates. `computeAshtakoota` in packages/astro-engine
 * produces every number and every `note`; this file only decides how they
 * look and what a first-time reader needs told alongside them. If a value
 * looks wrong, the bug is in the engine, not in this component.
 *
 * Copy rule for this screen: a koota name means nothing to someone opening an
 * astrology app for the first time, so every row carries the question that
 * koota is actually asking, plus an InfoDot into the glossary.
 */

type KootaId = KootaScore['koota'];

/** Display order is the classical one: 1 + 2 + 3 + 4 + 5 + 6 + 7 + 8 = 36. */
const KOOTA_ORDER: KootaId[] = [
  'varna',
  'vashya',
  'tara',
  'yoni',
  'grahaMaitri',
  'gana',
  'bhakoot',
  'nadi',
];

const KOOTA_META: Record<KootaId, { label: string; term: string; asks: string }> = {
  varna: {
    label: 'Varna',
    term: 'varna',
    asks: 'Whether your instincts about work, duty and role sit comfortably together.',
  },
  vashya: {
    label: 'Vashya',
    term: 'vashya',
    asks: 'Who naturally draws the other in — and whether that pull runs both ways.',
  },
  tara: {
    label: 'Tara',
    term: 'tara',
    asks: 'Counting from one birth star to the other, whether the count lands on a lucky number.',
  },
  yoni: {
    label: 'Yoni',
    term: 'yoni',
    asks: 'Physical and sexual compatibility, coded as an animal symbol for each birth star.',
  },
  grahaMaitri: {
    label: 'Graha Maitri',
    term: 'graha-maitri',
    asks: 'Whether the grahas that rule your two Chandra rashis are friends, strangers or enemies.',
  },
  gana: {
    label: 'Gana',
    term: 'gana',
    asks: 'Temperament — gentle, worldly or intense — and how those three mix.',
  },
  bhakoot: {
    label: 'Bhakoot',
    term: 'bhakoot',
    asks: 'The angle between your Chandra rashis, read traditionally as the health of a shared home.',
  },
  nadi: {
    label: 'Nadi',
    term: 'nadi',
    asks: 'Constitutional type. Carries the most points, and by far the most argument.',
  },
};

export interface KootaRange {
  min: number;
  max: number;
}

export interface DoshaFlags {
  /** Manglik status per side. 'unknown' when the birth time cannot support the rule. */
  mangalSelf: 'yes' | 'no' | 'unknown';
  mangalPartner: 'yes' | 'no' | 'unknown';
  /** Both Moons share a nadi — the koota scored 0 of 8. */
  nadiDosha: boolean;
  /** The Chandra rashis sit at a 2/12, 5/9 or 6/8 angle — the koota scored 0 of 7. */
  bhakootDosha: boolean;
}

export interface AshtakootaTableProps {
  result: AshtakootaResult;
  doshas: DoshaFlags;
  selfLabel: string;
  partnerLabel: string;
  /**
   * Present only when a birth time is not exact and the Moon could sit in more
   * than one sign or star. `result` is then one representative case, and these
   * are the spans across every case — showing a single confident total when the
   * inputs are a range would be a lie told with a decimal point.
   */
  totalRange?: KootaRange;
  ranges?: Partial<Record<KootaId, KootaRange>>;
  /**
   * Varna is directional in the classical texts. When reversing the two charts
   * changes its score, say so rather than presenting one order as the truth.
   */
  varnaReversed?: number;
}

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** The conventional reading of a total. Conventions, not laws — see the footer. */
function bandFor(total: number): { label: string; tone: 'ok' | 'gold' | 'danger'; gloss: string } {
  if (total >= 32) {
    return {
      label: 'Excellent',
      tone: 'ok',
      gloss: 'Rarely reached. Treated as an unusually strong match by anyone using this system.',
    };
  }
  if (total >= 25) {
    return {
      label: 'Very good',
      tone: 'ok',
      gloss: 'The band most families are hoping to land in.',
    };
  }
  if (total >= 18) {
    return {
      label: 'Acceptable',
      tone: 'gold',
      gloss: 'Above the threshold that is conventionally treated as a pass.',
    };
  }
  return {
    label: 'Below the conventional threshold',
    tone: 'danger',
    gloss:
      'Under 18 is where traditional practice would usually counsel caution. It describes an arithmetic result, not a person.',
  };
}

const BANDS: { range: string; label: string }[] = [
  { range: 'under 18', label: 'poor by convention' },
  { range: '18–24', label: 'acceptable' },
  { range: '25–31', label: 'very good' },
  { range: '32–36', label: 'excellent' },
];

export function AshtakootaTable({
  result,
  doshas,
  selfLabel,
  partnerLabel,
  totalRange,
  ranges,
  varnaReversed,
}: AshtakootaTableProps) {
  const byId = new Map(result.kootas.map((k) => [k.koota, k] as const));
  const rows = KOOTA_ORDER.map((id) => byId.get(id)).filter((k): k is KootaScore => !!k);
  const band = bandFor(result.total);
  const uncertain = !!totalRange && totalRange.min !== totalRange.max;

  const mangalMismatch =
    doshas.mangalSelf !== 'unknown' &&
    doshas.mangalPartner !== 'unknown' &&
    doshas.mangalSelf !== doshas.mangalPartner;
  const mangalBoth = doshas.mangalSelf === 'yes' && doshas.mangalPartner === 'yes';

  return (
    <section data-mode="vedic" className="col" style={{ gap: 10 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="mode-chip">Jyotish · sidereal</span>
        <span className="tiny faint">Moon-based</span>
      </div>
      <div className="mode-rule" />

      {/* --- total ------------------------------------------------------- */}
      <div
        className="card col"
        style={{ gap: 8, background: 'var(--mode-soft)', borderColor: 'var(--mode-line)' }}
      >
        <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
          <span
            style={{ fontSize: '2.4rem', fontWeight: 700, color: 'var(--mode-ink)', lineHeight: 1 }}
          >
            {fmt(result.total)}
          </span>
          <span className="muted">of {result.max} gunas</span>
          <InfoDot term="guna" />
        </div>
        <div className={`small ${band.tone === 'ok' ? 'accent' : band.tone === 'gold' ? 'gold' : 'danger'}`}>
          {band.label}
        </div>
        <div className="tiny muted" style={{ lineHeight: 1.55 }}>
          {band.gloss}
        </div>

        <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-2)', marginTop: 2 }}>
          <div
            style={{
              width: `${Math.max(0, Math.min(100, (result.total / result.max) * 100))}%`,
              height: '100%',
              borderRadius: 4,
              background: 'var(--mode)',
            }}
          />
        </div>

        <div className="wrap tiny faint" style={{ gap: 8, marginTop: 2 }}>
          {BANDS.map((b) => (
            <span key={b.range}>
              <span style={{ color: 'var(--mode-ink)' }}>{b.range}</span> {b.label}
            </span>
          ))}
        </div>

        {uncertain && totalRange && (
          <div className="banner banner--gold small" role="status">
            A birth time here is approximate, so the Moon could sit in more than one{' '}
            <Term id="rashi">rashi</Term> or <Term id="nakshatra">nakshatra</Term>. Across every
            possibility the total runs <strong>{fmt(totalRange.min)}</strong> to{' '}
            <strong>{fmt(totalRange.max)}</strong> of 36. The table below shows one representative
            case — treat the individual rows as indicative until an exact time is known.
          </div>
        )}
      </div>

      {/* --- the eight kootas -------------------------------------------- */}
      <div className="card col" style={{ gap: 0, padding: 10 }}>
        {rows.map((k, i) => {
          const meta = KOOTA_META[k.koota];
          const range = ranges?.[k.koota];
          const varies = !!range && range.min !== range.max;
          const zero = k.points === 0;
          return (
            <div
              key={k.koota}
              style={{
                padding: '10px 6px',
                borderTop: i === 0 ? undefined : '1px solid var(--border)',
              }}
            >
              <div className="spread" style={{ alignItems: 'center', gap: 8 }}>
                <div className="row" style={{ gap: 2, alignItems: 'center', minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{meta.label}</span>
                  <InfoDot term={meta.term} />
                </div>
                <span
                  className="small"
                  style={{ color: zero ? 'var(--danger)' : 'var(--mode-ink)', fontWeight: 700, flexShrink: 0 }}
                >
                  {varies && range ? `${fmt(range.min)}–${fmt(range.max)}` : fmt(k.points)}
                  <span className="faint" style={{ fontWeight: 400 }}> / {k.max}</span>
                </span>
              </div>

              <div
                style={{
                  height: 5,
                  borderRadius: 3,
                  background: 'var(--bg-2)',
                  margin: '6px 0 7px',
                }}
              >
                <div
                  style={{
                    width: `${(k.points / k.max) * 100}%`,
                    height: '100%',
                    borderRadius: 3,
                    background: zero ? 'var(--danger)' : 'var(--mode)',
                  }}
                />
              </div>

              <div className="tiny muted" style={{ lineHeight: 1.55 }}>
                {meta.asks}
              </div>
              {/* The engine's own note — the traceable part of the row. */}
              <div className="tiny faint" style={{ marginTop: 3, lineHeight: 1.5 }}>
                {k.note}
                {k.koota === 'varna' && varnaReversed !== undefined && varnaReversed !== k.points && (
                  <>
                    {' '}
                    Directional: scored with {selfLabel} first. Swap the two kundlis and this row
                    becomes {fmt(varnaReversed)} of 1 — the classical rule assumes a fixed order of
                    the two people, which a compatibility app cannot assume for you.
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* --- doshas ------------------------------------------------------- */}
      <div className="col" style={{ gap: 8 }}>
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          Dosha checks
          <InfoDot term="kundali-milan" />
        </div>

        {doshas.nadiDosha ? (
          <div className="banner banner--danger small">
            <strong>Nadi dosha.</strong> Both Moons fall in the same nadi, so this koota scores 0 of
            8 — the single largest block of points in the system. Traditional practice treats it as
            the most serious flag of the eight, and traditional practice also lists several
            cancellations for it. It is a classification of two birth stars, not a finding about
            either of you. <InfoDot term="nadi-dosha" />
          </div>
        ) : (
          <div className="small muted">
            No nadi dosha — the two Moons fall in different nadis.
          </div>
        )}

        {doshas.bhakootDosha ? (
          <div className="banner banner--danger small">
            <strong>Bhakoot dosha.</strong> Your <Term id="moon-sign">Chandra rashis</Term> sit at
            one of the angles the texts flag (2/12, 5/9 or 6/8), so this koota scores 0 of 7. It is conventionally read as friction
            in a shared household rather than in the attraction itself.{' '}
            <InfoDot term="bhakoot-dosha" />
          </div>
        ) : (
          <div className="small muted">
            No bhakoot dosha — the two Chandra rashis are at a clear angle.
          </div>
        )}

        {mangalBoth && (
          <div className="banner banner--info small">
            <strong>Both kundlis are Manglik.</strong> In classical practice two Manglik kundlis are
            considered to cancel the concern rather than compound it. <InfoDot term="manglik" />
          </div>
        )}
        {mangalMismatch && (
          <div className="banner banner--gold small">
            <strong>Mangal dosha on one side only</strong> — {doshas.mangalSelf === 'yes' ? selfLabel : partnerLabel}{' '}
            is Manglik and {doshas.mangalSelf === 'yes' ? partnerLabel : selfLabel} is not. This is
            the flag that causes the most anxiety in Indian matchmaking, and the one with the widest
            regional disagreement about what it means, how it is counted, and what cancels it.{' '}
            <InfoDot term="manglik" />
          </div>
        )}
        {(doshas.mangalSelf === 'unknown' || doshas.mangalPartner === 'unknown') && (
          <div className="small faint">
            Mangal dosha needs a birth time precise enough to place{' '}
            <Term id="graha.mangal">Mangal</Term> against the <Term id="lagna">Lagna</Term>, so it
            is not being asserted for at least one of these kundlis.
          </div>
        )}
      </div>

      <div className="tiny faint" style={{ lineHeight: 1.6 }}>
        Guna Milan was designed for families weighing a proposed marriage, and it answers that
        question in that context. It reads two Moon positions and nothing else — not what you have
        talked about, not how you treat each other, not whether either of you actually wants this. A
        high total is not consent and a low total is not a warning about a person.
      </div>
    </section>
  );
}
